import { flow } from "modules/common/models/flow";
import { apiUrls } from "modules/common/services/communication/urls";

export type UserAccessTarget = "employer" | "outsourcer";

/**
 * Ссылка доступа в карточке сотрудника или контрагента: состояние, отправка, отзыв.
 * Подмешивается в модели карточек через .actions((self) => accessLinkActions(self, urls)).
 */

export interface AccessLinkInfo {
  purpose: "invite" | "reset";
  email: string;
  created: string;
  expires: string;
  author: { id: string; name: string } | null;
}

/** Доступ к приложению отключён: когда, кто, почему */
export interface AccessBlockInfo {
  at: string;
  by: string;
  reason: string;
}

export interface AccessStatus {
  hasPassword: boolean;
  login: string | null;
  email: string | null;
  link: AccessLinkInfo | null;
  blocked: AccessBlockInfo | null;
}

interface AccessLinkHost {
  id: string;
  transport: {
    get<T>(url: string): Promise<T>;
    post<T>(url: string, data?: any): Promise<T>;
    delete<T>(url: string, config?: any): Promise<T>;
  };
  notify: { success(message: string): void; error(error: any): void };
}

export const accessLinkActions = (
  self: AccessLinkHost,
  urls: { status: (id: string) => string; link: (id: string) => string; target: UserAccessTarget }
) => ({
  loadAccess: flow(function* () {
    try {
      const status: AccessStatus = yield self.transport.get<AccessStatus>(urls.status(self.id));
      return status;
    } catch (er) {
      self.notify.error(er);
      return null;
    }
  }),

  sendAccessLink: flow(function* () {
    try {
      const status: AccessStatus = yield self.transport.post<AccessStatus>(urls.link(self.id), {});
      self.notify.success(
        status.link?.purpose === "reset" ? "Ссылка для смены пароля отправлена" : "Приглашение отправлено"
      );
      return status;
    } catch (er) {
      self.notify.error(er);
      return null;
    }
  }),

  /** Отключить доступ к приложению: человек сразу выходит и не может войти */
  blockAccess: flow(function* (reason: string) {
    try {
      yield self.transport.post<any>(apiUrls.userAccess.block(urls.target, self.id), { reason });
      self.notify.success("Доступ к приложению отключён");
      const status: AccessStatus = yield self.transport.get<AccessStatus>(urls.status(self.id));
      return status;
    } catch (er) {
      self.notify.error(er);
      return null;
    }
  }),

  unblockAccess: flow(function* () {
    try {
      yield self.transport.post<any>(apiUrls.userAccess.unblock(urls.target, self.id));
      self.notify.success("Доступ к приложению включён");
      const status: AccessStatus = yield self.transport.get<AccessStatus>(urls.status(self.id));
      return status;
    } catch (er) {
      self.notify.error(er);
      return null;
    }
  }),

  /** Данные для входа на почту - сразу после генерации пароля */
  mailCredentials: flow(function* (password: string) {
    try {
      const url =
        urls.target === "employer"
          ? apiUrls.accessLinks.employeeCredentialsMail(self.id)
          : apiUrls.accessLinks.outsourcerCredentialsMail(self.id);
      yield self.transport.post<boolean>(url, { password });
      self.notify.success("Данные для входа отправлены на почту");
      return true;
    } catch (er) {
      self.notify.error(er);
      return false;
    }
  }),

  revokeAccessLink: flow(function* () {
    try {
      const status: AccessStatus = yield self.transport.delete<AccessStatus>(urls.link(self.id));
      self.notify.success("Ссылка отозвана");
      return status;
    } catch (er) {
      self.notify.error(er);
      return null;
    }
  }),
});
